import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  MouseEvent,
  KeyboardEvent,
  FocusEvent,
} from "react";
import "./dropdown.scss";
import {
  DownArrowIcon,
  SearchIcon,
  UpArrowIconDropdown,
} from "../../../assets/Icons";

export type Option = {
  key: string;
  value: string;
};

export type DropdownProps = {
  id: string;
  options: Option[];
  placeholder?: string;
  value?: Option | null;
  onSelect: (selectedOption: Option) => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  attributeName: string;
  onInputChange?: (typedValue: string) => void;
  showOthersOption: boolean;
  clearAttributeValues?: () => void;
  isNumeric?: boolean;
  disableSearch?: boolean;
};
const Dropdown: React.FC<DropdownProps> = ({
  id,
  options,
  placeholder = "Select...",
  value = null,
  onSelect,
  openDropdownId,
  setOpenDropdownId,
  onInputChange,
  showOthersOption,
  attributeName,
  clearAttributeValues,
  isNumeric,
  disableSearch = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(value ? value.value : "");
  const [isEditing, setIsEditing] = useState(false);
  const [ddlOpen, setDdlOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const optionsRefs = useRef<(HTMLLIElement | null)[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isProgrammaticBlurRef = useRef(false);

  const onBlur = (event: any) => {
    // Don't close dropdown if it's a programmatic blur (keyboard closing)
    if (isProgrammaticBlurRef.current) {
      return;
    }
    
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDdlOpen(false);
    }
  };
  const onFocus = () => {
    setDdlOpen(true);
  };

  useEffect(() => {
    if (!isEditing) {
      setSearchTerm(value ? value.value : "");
    }
  }, [value, isEditing]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => a.value.localeCompare(b.value));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (disableSearch) return sortedOptions;
    const lowerTerm = searchTerm.toLowerCase().trim();
  
    if (attributeName !== 'Model Number') {
      return sortedOptions.filter((option) =>
        option.value.toLowerCase().trim().includes(lowerTerm)
      );
    } else {
      const uniqueOptions = new Map<string, Option>(); // To store unique options
  
      sortedOptions.forEach(option => {
        const [modelNumberRaw, modelNameRaw] = option.value.split('__');
        const modelName = modelNameRaw?.trim() || option.value;
        const modelNumber = modelNumberRaw.trim();
  
        if (
          modelName.toLowerCase().includes(lowerTerm) ||
          modelNumber.toLowerCase().includes(lowerTerm)
        ) {
          if (!uniqueOptions.has(modelName)) {
            uniqueOptions.set(modelName, {
              key: option.value,
              value: modelName
            });
          }
        }
      });
  
      // Convert map to array and sort
      let results = Array.from(uniqueOptions.values()).sort((a, b) =>
        a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' })
      );
  
      // If no match found and searchTerm exists, push custom entry
      if (results.length === 0 && searchTerm) {
        results.push({
          key: searchTerm, 
          value: searchTerm
        });
      }
  
      return results;
    }
  }, [searchTerm, sortedOptions, attributeName]);
  

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions]);

  useEffect(() => {
    if (ddlOpen && optionsRefs.current[highlightedIndex]) {
      optionsRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex, ddlOpen]);

  const handleSelect = (option: Option) => {
    setSearchTerm(option.value);
    onSelect(option);
    setIsEditing(false);
    setDdlOpen(false);
  };


  const handleDropdownTouch = () => {
    // Close keyboard when touching the dropdown list (but keep dropdown open)
    if (inputRef.current) {
      isProgrammaticBlurRef.current = true;
      inputRef.current.blur();
      // Reset the flag after a short delay
      setTimeout(() => {
        isProgrammaticBlurRef.current = false;
      }, 100);
    }
  };

  const handleArrowClick = (e: MouseEvent) => {
    e.stopPropagation();
    const nextOpen = !ddlOpen;
    setDdlOpen(nextOpen);
    if (nextOpen) {
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disableSearch) return;
    const value = isNumeric
      ? e.target.value.replace(/[^0-9]/g, "")
      : e.target.value;
    setSearchTerm(value);
    setIsEditing(true);
    onInputChange?.(value);
    setDdlOpen(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (filteredOptions[highlightedIndex]) {
        handleSelect(filteredOptions[highlightedIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : prev
      );
    } else if (e.key === "Backspace" || e.key === "Delete") {
      if (!ddlOpen) {
        setDdlOpen(true);
        setIsEditing(true);
      }
    }
  };
  

  const clearSearch = (e: MouseEvent) => {
    e.stopPropagation();
    setSearchTerm("");
    onSelect({ key: "", value: "" });
    setDdlOpen(false);
    if (clearAttributeValues) {
      clearAttributeValues();
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="dropdown"
      onFocus={onFocus}
      tabIndex={0}
      onBlur={onBlur}
    >
      <div className={`dropdown-header ${ddlOpen ? "open" : ""}`}>
        <div className={`dropdown-input-container${disableSearch ? " no-search" : ""}`}>
          {!disableSearch && <SearchIcon />}
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            maxLength={100}
            readOnly={disableSearch}
            style={disableSearch ? { cursor: "pointer" } : undefined}
          />
          {!disableSearch && searchTerm && (
            <button className="dropdown-clear-button" onClick={clearSearch}>
              {"\u00D7"}
            </button>
          )}
          <div onClick={handleArrowClick} className="dropdown-arrow">
            {ddlOpen ? <UpArrowIconDropdown /> : <DownArrowIcon />}
          </div>
        </div>
      </div>

      {ddlOpen && (
        <ul
          className="dropdown-list"
          onTouchStart={handleDropdownTouch}
          onWheel={(e) => {
            const { currentTarget } = e;
            // Prevent scroll propagation when at scroll limits
            if (
              (e.deltaY < 0 && currentTarget.scrollTop === 0) || // Scrolling up at top
              (e.deltaY > 0 && currentTarget.scrollHeight - currentTarget.scrollTop === currentTarget.clientHeight) // Scrolling down at bottom
            ) {
              e.preventDefault();
            }
          }}
          onTouchMove={(e) => {
            const { currentTarget } = e;
            const touch = e.touches[0];
            if (touch) {
              // Calculate the difference from the last touch position (if available) to determine scroll direction
              // This is a simplified check, more robust solutions might store last touch position
              const isScrollingUp = currentTarget.dataset.lastTouchY && touch.clientY > parseFloat(currentTarget.dataset.lastTouchY);
              const isScrollingDown = currentTarget.dataset.lastTouchY && touch.clientY < parseFloat(currentTarget.dataset.lastTouchY);

              if (
                (isScrollingUp && currentTarget.scrollTop === 0) || // Scrolling up at top
                (isScrollingDown && currentTarget.scrollHeight - currentTarget.scrollTop === currentTarget.clientHeight) // Scrolling down at bottom
              ) {
                e.preventDefault();
              }
              currentTarget.dataset.lastTouchY = String(touch.clientY);
            }
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={`${option.key}-${index}`}
                ref={(el) => {
                  optionsRefs.current[index] = el;
                }}
                className={`dropdown-item ${
                  highlightedIndex === index ? "highlighted" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(option);
                }}
              >
                {option.value.charAt(0).toUpperCase() + option.value.slice(1)}
              </li>
            ))
          ) : (
            <li className="dropdown-no-results">No results found</li>
          )}

          {showOthersOption && (
            <li
              key="other"
              className="dropdown-item"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect({ key: "other", value: "Other/I'm not sure" });
              }}
            >
              Other/I'm not sure
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
